'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'

import {
  getGetTradingAccountQueryKey,
  getListTradingAccountsQueryKey,
  type TradingAccountEditResponse,
  useEditTradingAccount,
  useUpdateTradingAccount,
} from '@api/orval/apiHooks'
import { DynamicActions } from '@components/DynamicActions/DynamicActions'
import { TextInputField } from '@components/Form/FormFields'
import { toApiPayload } from '@components/Form/formHelpers'
import { Alert, Button, DialogContent, DialogHeader, DialogTitle, Spin } from '@fp/ui-kit'
import { emitToast } from '@lib/emitToast'
import { EditAccountFormValues, tradingAccountFormSchema } from '@lib/formSchemas'

type FormFields = { name: EditAccountFormField; label: string }

type EditAccountFormField = keyof EditAccountFormValues

type EditAccountFormProps = {
  accountId: number
  onCancel: () => void
}

const fields: FormFields[] = [
  { name: 'min_trading_days', label: 'Min Trading Days' },
  { name: 'max_loss_percentage', label: 'Max Loss %' },
  { name: 'profit_target_percentage', label: 'Profit Target %' },
  { name: 'max_daily_loss_percentage', label: 'Max Daily Loss %' },
  { name: 'max_challenge_duration_days', label: 'Max Challenge Duration (Days)' },
  { name: 'profit_split_percentage', label: 'Profit Split %' },
  { name: 'daily_lotsize_limit', label: 'Daily Lotsize Limit' },
  { name: 'max_daily_floating_risk', label: 'Max Daily Floating Risk' },
]

type EditAccountFieldsProps = EditAccountFormProps & { data: TradingAccountEditResponse }

const EditAccountFields = ({ accountId, data, onCancel }: EditAccountFieldsProps) => {
  const queryClient = useQueryClient()

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditAccountFormValues>({
    resolver: zodResolver(tradingAccountFormSchema),
    mode: 'onBlur',
    defaultValues: data,
  })

  const { mutate, isPending } = useUpdateTradingAccount({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListTradingAccountsQueryKey() })
        queryClient.invalidateQueries({ queryKey: getGetTradingAccountQueryKey(accountId) })
        onCancel()
        emitToast({
          message: 'Trading account updated successfully',
          type: 'success',
        })
      },
    },
  })

  const onSubmit = (values: EditAccountFormValues) => {
    mutate({ id: accountId, data: { trading_account: toApiPayload(values) } })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='space-y-6 max-w-xl'>
      <div className='grid sm:grid-cols-2 gap-4 justify-center'>
        {fields.map(({ name, label }) => (
          <TextInputField
            key={name}
            name={name}
            label={label}
            control={control}
            type='number'
            min={0}
            error={errors[name]?.message}
          />
        ))}
      </div>

      <Button type='submit' size='block' variant='primary' disabled={isPending} isLoading={isPending}>
        Save
      </Button>
    </form>
  )
}

const EditAccountContent = ({ accountId, onCancel }: EditAccountFormProps) => {
  // defaultValues are read once, so every open must load fresh data.
  const { data, isError } = useEditTradingAccount(accountId, { query: { gcTime: 0 } })

  return (
    <>
      <DialogHeader>
        <div className='flex items-center justify-between gap-3'>
          <DialogTitle>Edit Account</DialogTitle>
          {data && (
            <DynamicActions
              type='trading_account'
              itemId={accountId}
              actions={data.actions}
              actionFilter='hide_all'
              className='mr-4'
            />
          )}
        </div>
      </DialogHeader>

      {isError && <Alert variant='destructive'>Failed to load the account.</Alert>}
      {!isError && !data && <Spin size='block' />}
      {data && <EditAccountFields accountId={accountId} data={data} onCancel={onCancel} />}
    </>
  )
}

export const EditAccountForm = ({ accountId, onCancel }: EditAccountFormProps) => (
  <DialogContent className='sm:min-w-lg'>
    <EditAccountContent accountId={accountId} onCancel={onCancel} />
  </DialogContent>
)
